import React from 'react';
import { Button, DotLoading, PullToRefresh } from 'antd-mobile';
import { AppOutline, CheckCircleFill, CouponOutline } from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocaleSwitcher } from '../../../components/LocaleSwitcher';
import { dashboardPageStyles } from './styles';
import { PromotionGuideData } from './types';

interface DashboardViewProps {
  loading: boolean;
  data: PromotionGuideData;
  onRefresh: () => Promise<void>;
}

const MANUAL_FLOW_KEYS = ['claim', 'send', 'confirm', 'threshold', 'redeem'] as const;
const FAQ_KEYS = ['loginTiming', 'redeemCheck', 'appRequired'] as const;

export const DashboardView: React.FC<DashboardViewProps> = ({ loading, data, onRefresh }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const overview = data.overview;
  const threshold = overview?.redeem_threshold || 10;

  if (loading) {
    return (
      <div className="webview-page" style={{ ...dashboardPageStyles.page, ...dashboardPageStyles.loadingPage }}>
        <DotLoading />
      </div>
    );
  }

  return (
    <div className="webview-page" style={dashboardPageStyles.page}>
      <PullToRefresh onRefresh={onRefresh} headHeight={0}>
        <div style={dashboardPageStyles.topBar}>
          <div style={dashboardPageStyles.brandRow}>
            <div style={dashboardPageStyles.brandMark}>7966</div>
            <div>
              <div style={dashboardPageStyles.brandTitle}>{t('brand')}</div>
              <div style={dashboardPageStyles.brandSubtitle}>{t('brandSubtitle')}</div>
            </div>
          </div>
          <LocaleSwitcher tone="light" labelMode="short" style={{ minWidth: 74, minHeight: 36 }} />
        </div>

        <section style={dashboardPageStyles.hero}>
          <div style={dashboardPageStyles.heroEyebrow}>
            <CheckCircleFill fontSize={14} />
            <span>{t('heroEyebrow')}</span>
          </div>
          <h1 style={dashboardPageStyles.heroTitle}>{t('heroTitle')}</h1>
          <div style={dashboardPageStyles.heroText}>{t('heroText')}</div>
          <div style={dashboardPageStyles.heroActions}>
            <Button
              block
              color="primary"
              style={dashboardPageStyles.primaryButton}
              onClick={() => navigate('/download')}
            >
              {t('downloadApp')}
            </Button>
            <Button
              block
              fill="outline"
              style={dashboardPageStyles.secondaryButton}
              onClick={() => navigate('/manual-activity')}
            >
              {t('startManual')}
            </Button>
          </div>

        </section>

        <section style={dashboardPageStyles.section}>
          <div style={dashboardPageStyles.sectionTitle}>{t('methods.title')}</div>
          <div style={dashboardPageStyles.sectionText}>{t('methods.subtitle')}</div>
          <div style={dashboardPageStyles.methodGrid}>
            <MethodCard
              icon={<CouponOutline />}
              badge={t('methods.manual.badge')}
              title={t('methods.manual.title')}
              description={t('methods.manual.description')}
              buttonText={t('methods.manual.button')}
              onClick={() => navigate('/manual-activity')}
            />
            <MethodCard
              icon={<AppOutline />}
              badge={t('methods.app.badge')}
              title={t('methods.app.title')}
              description={t('methods.app.description')}
              buttonText={t('methods.app.button')}
              onClick={() => navigate('/download')}
              primary
            />
          </div>
        </section>

        <section style={dashboardPageStyles.section}>
          <div style={dashboardPageStyles.sectionTitle}>{t('flow.title')}</div>
          <div style={dashboardPageStyles.sectionText}>{t('flow.subtitle')}</div>
          <div style={dashboardPageStyles.notice}>{t('flow.notice', { threshold })}</div>
          <div style={dashboardPageStyles.flowCard}>
            {MANUAL_FLOW_KEYS.map((key, index) => (
              <div
                key={key}
                style={{
                  ...dashboardPageStyles.flowItem,
                  ...(index === MANUAL_FLOW_KEYS.length - 1 ? dashboardPageStyles.flowItemLast : {}),
                }}
              >
                <div style={dashboardPageStyles.flowIndex}>{index + 1}</div>
                <div>
                  <div style={dashboardPageStyles.flowTitle}>{t(`flow.steps.${key}.title`)}</div>
                  <div style={dashboardPageStyles.flowDesc}>{t(`flow.steps.${key}.description`, { threshold })}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={dashboardPageStyles.section}>
          <div style={dashboardPageStyles.sectionTitle}>{t('redeem.title')}</div>
          <div style={dashboardPageStyles.redemptionCard}>
            <div style={dashboardPageStyles.redemptionTitle}>{t('redeem.cardTitle')}</div>
            <div style={dashboardPageStyles.redemptionText}>{t('redeem.description', { threshold })}</div>
            <Button
              block
              color="primary"
              style={{ ...dashboardPageStyles.primaryButton, marginTop: 14 }}
              onClick={() => navigate('/manual-activity')}
            >
              {t('redeem.button')}
            </Button>
          </div>
        </section>

        <section style={dashboardPageStyles.section}>
          <div style={dashboardPageStyles.sectionTitle}>{t('privacy.title')}</div>
          <div style={dashboardPageStyles.sectionText}>{t('privacy.description')}</div>
        </section>

        <section style={dashboardPageStyles.section}>
          <div style={dashboardPageStyles.sectionTitle}>{t('faq.title')}</div>
          <div style={dashboardPageStyles.faqList}>
            {FAQ_KEYS.map((key) => (
              <div key={key} style={dashboardPageStyles.faqItem}>
                <div style={dashboardPageStyles.faqQuestion}>{t(`faq.items.${key}.question`)}</div>
                <div style={dashboardPageStyles.faqAnswer}>{t(`faq.items.${key}.answer`, { threshold })}</div>
              </div>
            ))}
          </div>
        </section>
      </PullToRefresh>
    </div>
  );
};

interface MethodCardProps {
  icon: React.ReactNode;
  badge: string;
  title: string;
  description: string;
  buttonText: string;
  onClick: () => void;
  primary?: boolean;
}

const MethodCard: React.FC<MethodCardProps> = ({
  icon,
  badge,
  title,
  description,
  buttonText,
  onClick,
  primary,
}) => (
  <div style={dashboardPageStyles.methodCard}>
    <div style={dashboardPageStyles.methodHead}>
      <div style={dashboardPageStyles.methodIcon}>{icon}</div>
      <div
        style={{
          ...dashboardPageStyles.methodBadge,
          ...(primary ? {} : { background: '#eef4ff', color: '#1769ff' }),
        }}
      >
        {badge}
      </div>
    </div>
    <div style={dashboardPageStyles.methodTitle}>{title}</div>
    <div style={dashboardPageStyles.methodDesc}>{description}</div>
    <Button
      block
      color={primary ? 'primary' : 'default'}
      fill={primary ? 'solid' : 'outline'}
      style={dashboardPageStyles.methodButton}
      onClick={onClick}
    >
      {buttonText}
    </Button>
  </div>
);
